/**
 * The three edits a warehouse supports: its basic fields, what it restocks
 * from, and deactivating it.
 *
 * Takes the record hook's state so a save is reflected without a refetch.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { updateWarehouse, updateRestockSource, deactivateWarehouse } from '../../api/warehouse';

import type { useWarehouseRecord } from './useWarehouseRecord';

export function useWarehouseMutations(record: ReturnType<typeof useWarehouseRecord>) {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const { warehouse, setWarehouse, setError, formData } = record;

  /** Announce a success and clear it, so the banner does not persist. */
  function announce(message: string) {
    setSuccess(message);
    setTimeout(() => setSuccess(null), 3000);
  }

  async function run(work: () => Promise<void>, failure: string) {
    setSaving(true);
    setError(null);
    try {
      await work();
    } catch (err) {
      setError(err instanceof Error ? err.message : failure);
    } finally {
      setSaving(false);
    }
  }

  async function saveBasicInfo() {
    if (!warehouse) return;
    await run(async () => {
      const updated = await updateWarehouse(warehouse.id, {
        name: formData.name,
        code: formData.code || null,
        description: formData.description || null,
      });
      setWarehouse({ ...warehouse, ...updated });
      announce('Warehouse updated successfully');
    }, 'Failed to update warehouse');
  }

  async function changeRestockSource(sourceWarehouseId: string | null) {
    if (!warehouse) return;
    await run(async () => {
      await updateRestockSource(warehouse.id, sourceWarehouseId);
      setWarehouse({ ...warehouse, sourceWarehouseId });
      record.setSelectedRestockSourceId(sourceWarehouseId);
      announce('Restock source updated');
    }, 'Failed to update restock source');
  }

  async function deactivate() {
    if (!warehouse || !confirm('Deactivate this warehouse?')) return;
    // Navigates away on success, so it does not clear `saving` itself.
    setSaving(true);
    try {
      await deactivateWarehouse(warehouse.id);
      navigate('/admin/settings/warehouses');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate warehouse');
      setSaving(false);
    }
  }

  return { saving, success, saveBasicInfo, changeRestockSource, deactivate };
}
